import { create } from 'zustand';
import { InputStroke, WorkpadConfig, StepProgress } from '@eduwrite/shared';

interface WorkpadState {
    isOpen: boolean;
    mode: 'free_draw' | 'guided_steps' | 'none';
    freeDrawStrokes: InputStroke[];
    stepProgress: StepProgress[];
    currentStepIndex: number;
    hintVisible: boolean;
    workingOut: { mode: 'free_draw' | 'guided_steps'; strokes?: InputStroke[]; stepProgress?: StepProgress[]; hintsUsed?: number; totalTimeMs?: number } | null;
    activeConfig: WorkpadConfig | null; // WP-028 — retained so dynamic choice scaffolds pass steps to WorkpadSheet

    open: (config: WorkpadConfig) => void;
    close: () => void;
    addStroke: (stroke: InputStroke) => void;
    popStroke: () => void;
    clearStrokes: () => void;
    advanceStep: () => void;
    revealHint: () => void;
    recordStepInput: (stepId: string, input: string | InputStroke[]) => void;
    markStepComplete: (stepId: string) => void;
    reset: () => void;
}

export const useWorkpadStore = create<WorkpadState>((set, get) => ({
    isOpen: false,
    mode: 'none',
    freeDrawStrokes: [],
    stepProgress: [],
    currentStepIndex: 0,
    hintVisible: false,
    workingOut: null,
    activeConfig: null,

    open: (config) => {
        if (!config.enabled || config.mode === 'none') return;
        set({
            isOpen: true,
            mode: config.mode,
            freeDrawStrokes: [],
            stepProgress: config.steps?.map((s) => ({
                stepId: s.id,
                completed: false,
                timeSpentMs: 0,
                hintRevealed: false,
            })) || [],
            currentStepIndex: 0,
            hintVisible: false,
            workingOut: null,
            activeConfig: config,
        });
    },

    close: () => {
        const { mode, freeDrawStrokes, stepProgress } = get();
        const hintsUsed = stepProgress.filter((s) => s.hintRevealed).length;
        const totalTimeMs = stepProgress.reduce((sum, s) => sum + s.timeSpentMs, 0);

        const workingOut =
            mode === 'free_draw'
                ? { mode: 'free_draw' as const, strokes: freeDrawStrokes, hintsUsed, totalTimeMs }
                : { mode: 'guided_steps' as const, stepProgress, hintsUsed, totalTimeMs };

        set({ isOpen: false, workingOut, activeConfig: null });
    },

    addStroke: (stroke) =>
        set((state) => ({
            freeDrawStrokes: [...state.freeDrawStrokes, stroke],
        })),

    popStroke: () =>
        set((state) => ({
            freeDrawStrokes: state.freeDrawStrokes.slice(0, -1),
        })),
    clearStrokes: () => set({ freeDrawStrokes: [] }),

    advanceStep: () =>
        set((state) => {
            if (state.currentStepIndex < state.stepProgress.length - 1) {
                return { currentStepIndex: state.currentStepIndex + 1, hintVisible: false };
            }
            return state;
        }),

    revealHint: () =>
        set((state) => {
            const stepId = state.stepProgress[state.currentStepIndex]?.stepId;
            if (!stepId) return state;
            return {
                hintVisible: true,
                stepProgress: state.stepProgress.map((sp) =>
                    sp.stepId === stepId ? { ...sp, hintRevealed: true } : sp
                ),
            };
        }),

    recordStepInput: (stepId, input) =>
        set((state) => ({
            stepProgress: state.stepProgress.map((sp) =>
                sp.stepId === stepId ? { ...sp, studentInput: input } : sp
            ),
        })),

    markStepComplete: (stepId) =>
        set((state) => ({
            stepProgress: state.stepProgress.map((sp) =>
                sp.stepId === stepId ? { ...sp, completed: true } : sp
            ),
        })),

    reset: () =>
        set({
            isOpen: false,
            mode: 'none',
            freeDrawStrokes: [],
            stepProgress: [],
            currentStepIndex: 0,
            hintVisible: false,
            workingOut: null,
            activeConfig: null,
        }),
}));
